import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Template from "../../components/Template";
import api from "../../services/api";
import patrimonioService from "../../services/patrimonioService";
import SuccessModal from "../../components/SuccessModal";
import AlertDialog from "../../components/AlertDialog";
import { maskCurrency } from "../../utils/masks";
import { TIPOS_PATRIMONIO, SITUACOES_PATRIMONIO } from "./patrimonioConstants";
import CamposEspecificosTipo, { CAMPOS_ESPECIFICOS_INICIAIS, appendCamposEspecificos } from "./CamposEspecificosTipo";

// Converte valor mascarado ("R$ 1.234,56") em número ou string vazia
const parseCurrency = (value) => {
    if (!value) return "";
    const parsed = parseFloat(String(value).replace(/[^\d,]/g, "").replace(",", "."));
    return isNaN(parsed) ? "" : parsed;
};

const CadastrarPatrimonio = () => {
    const navigate = useNavigate();

    const [locais, setLocais] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [obras, setObras] = useState([]);
    const [fornecedores, setFornecedores] = useState([]);

    const [localizacaoTipo, setLocalizacaoTipo] = useState("local"); // local | obra | nenhum
    const [fotos, setFotos] = useState([]);

    const [formData, setFormData] = useState({
        codigo: "",
        nome: "",
        descricao: "",
        tipo: "EQUIPAMENTO",
        categoriaId: "",
        situacao: "ATIVO",
        numeroSerie: "",
        marca: "",
        modelo: "",
        ...CAMPOS_ESPECIFICOS_INICIAIS,
        localId: "",
        obraId: "",
        responsavel: "",
        valorAquisicao: "",
        dataAquisicao: "",
        fornecedorId: "",
        numeroNotaFiscal: "",
        garantiaAte: "",
        vidaUtilMeses: "",
        valorResidual: "",
        observacoes: "",
    });

    const [isSaving, setIsSaving] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [codigoRes, locaisRes, categoriasRes, obrasRes, fornecedoresRes] = await Promise.all([
                    patrimonioService.proximoCodigo(),
                    patrimonioService.listarLocais({ ativo: true }),
                    patrimonioService.listarCategorias({ ativo: true }),
                    api.get("/obras/listar/todas"),
                    api.get("/fornecedores/todos"),
                ]);
                setFormData((prev) => ({ ...prev, codigo: codigoRes.codigo || "" }));
                setLocais(locaisRes || []);
                setCategorias(categoriasRes || []);
                setObras(obrasRes.data || []);
                setFornecedores(fornecedoresRes.data || []);
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
            }
        };
        fetchData();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
            // Trocar o tipo invalida a categoria selecionada
            ...(name === "tipo" ? { categoriaId: "" } : {}),
        }));
    };

    const handleCurrencyChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: maskCurrency(value) }));
    };

    const handleFotosChange = (e) => {
        setFotos(Array.from(e.target.files || []));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.nome.trim()) {
            setAlertMessage("Informe o nome do bem.");
            return;
        }

        const data = new FormData();
        data.append("codigo", formData.codigo);
        data.append("nome", formData.nome);
        data.append("descricao", formData.descricao);
        data.append("tipo", formData.tipo);
        data.append("categoriaId", formData.categoriaId);
        data.append("situacao", formData.situacao);
        data.append("numeroSerie", formData.numeroSerie);
        data.append("marca", formData.marca);
        data.append("modelo", formData.modelo);
        appendCamposEspecificos(data, formData);
        if (localizacaoTipo === "local" && formData.localId) data.append("localId", formData.localId);
        if (localizacaoTipo === "obra" && formData.obraId) data.append("obraId", formData.obraId);
        data.append("responsavel", formData.responsavel);
        data.append("valorAquisicao", parseCurrency(formData.valorAquisicao));
        data.append("dataAquisicao", formData.dataAquisicao);
        data.append("fornecedorId", formData.fornecedorId);
        data.append("numeroNotaFiscal", formData.numeroNotaFiscal);
        data.append("garantiaAte", formData.garantiaAte);
        data.append("vidaUtilMeses", formData.vidaUtilMeses);
        data.append("valorResidual", parseCurrency(formData.valorResidual));
        data.append("observacoes", formData.observacoes);

        fotos.forEach((foto, index) => {
            data.append(`fotos[${index}]`, foto);
        });

        setIsSaving(true);
        try {
            await patrimonioService.criar(data);
            setShowSuccessDialog(true);
        } catch (error) {
            console.error("Erro ao cadastrar patrimônio:", error);
            setAlertMessage(error.response?.data?.error || "Erro ao cadastrar patrimônio.");
        } finally {
            setIsSaving(false);
        }
    };

    const inputClass = "px-3 py-2 border border-slate-300 rounded-md text-sm w-full";
    const labelClass = "text-sm text-slate-700 font-medium";

    return (
        <Template>
            <div className="w-full px-4 py-6 flex flex-col gap-6">
                <h1 className="text-2xl text-stone-800 font-medium">Cadastrar Patrimônio</h1>

                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                    {/* Dados gerais */}
                    <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                        <div className="text-stone-600 text-base font-medium">Dados Gerais</div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className={labelClass}>Código</label>
                                <input
                                    type="text"
                                    name="codigo"
                                    value={formData.codigo}
                                    onChange={handleInputChange}
                                    className={inputClass}
                                    placeholder="PAT-0001"
                                />
                            </div>
                            <div className="flex flex-col gap-1.5 md:col-span-2">
                                <label className={labelClass}>Nome do bem *</label>
                                <input
                                    type="text"
                                    name="nome"
                                    value={formData.nome}
                                    onChange={handleInputChange}
                                    className={inputClass}
                                    placeholder="Ex: Notebook Dell Latitude 5440"
                                    required
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className={labelClass}>Tipo *</label>
                                <select name="tipo" value={formData.tipo} onChange={handleInputChange} className={inputClass} required>
                                    {TIPOS_PATRIMONIO.map((t) => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className={labelClass}>Categoria</label>
                                <select name="categoriaId" value={formData.categoriaId} onChange={handleInputChange} className={inputClass}>
                                    <option value="">Selecionar</option>
                                    {categorias
                                        .filter((c) => c.tipo === formData.tipo)
                                        .map((c) => (
                                            <option key={c.id} value={c.id}>{c.nome}</option>
                                        ))}
                                </select>
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className={labelClass}>Situação</label>
                                <select name="situacao" value={formData.situacao} onChange={handleInputChange} className={inputClass}>
                                    {SITUACOES_PATRIMONIO.filter((s) => ["ATIVO", "EM_MANUTENCAO"].includes(s.value)).map((s) => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className={labelClass}>Nº de Série</label>
                                <input type="text" name="numeroSerie" value={formData.numeroSerie} onChange={handleInputChange} className={inputClass} />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className={labelClass}>Marca</label>
                                <input type="text" name="marca" value={formData.marca} onChange={handleInputChange} className={inputClass} />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className={labelClass}>Modelo</label>
                                <input type="text" name="modelo" value={formData.modelo} onChange={handleInputChange} className={inputClass} />
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className={labelClass}>Descrição</label>
                            <textarea name="descricao" rows={2} value={formData.descricao} onChange={handleInputChange} className={inputClass} />
                        </div>
                    </div>

                    {/* Campos específicos do tipo (Veículo / Imóvel) */}
                    <CamposEspecificosTipo tipo={formData.tipo} formData={formData} setFormData={setFormData} />

                    {/* Localização inicial */}
                    <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                        <div className="text-stone-600 text-base font-medium">Localização Inicial</div>

                        <div className="flex flex-wrap gap-4 text-sm">
                            <label className="inline-flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="localizacaoTipo"
                                    checked={localizacaoTipo === "local"}
                                    onChange={() => setLocalizacaoTipo("local")}
                                />
                                <span>Local (Sede, Filial, Home Office...)</span>
                            </label>
                            <label className="inline-flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="localizacaoTipo"
                                    checked={localizacaoTipo === "obra"}
                                    onChange={() => setLocalizacaoTipo("obra")}
                                />
                                <span>Obra</span>
                            </label>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {localizacaoTipo === "local" ? (
                                <div className="flex flex-col gap-1.5">
                                    <label className={labelClass}>Local</label>
                                    <select name="localId" value={formData.localId} onChange={handleInputChange} className={inputClass}>
                                        <option value="">Selecionar</option>
                                        {locais.map((l) => (
                                            <option key={l.id} value={l.id}>{l.nome}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-1.5">
                                    <label className={labelClass}>Obra</label>
                                    <select name="obraId" value={formData.obraId} onChange={handleInputChange} className={inputClass}>
                                        <option value="">Selecionar</option>
                                        {obras.map((o) => (
                                            <option key={o.id} value={o.id}>
                                                {o.codigoProjeto ? `${o.codigoProjeto} - ` : ""}{o.nome || o.nomeProjeto}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="flex flex-col gap-1.5">
                                <label className={labelClass}>Responsável</label>
                                <input
                                    type="text"
                                    name="responsavel"
                                    value={formData.responsavel}
                                    onChange={handleInputChange}
                                    className={inputClass}
                                    placeholder="Nome da pessoa responsável pelo bem"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Aquisição e Garantia */}
                    <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                        <div className="text-stone-600 text-base font-medium">Aquisição e Garantia</div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className={labelClass}>Valor de Aquisição (R$)</label>
                                <input
                                    type="text"
                                    name="valorAquisicao"
                                    value={formData.valorAquisicao}
                                    onChange={handleCurrencyChange}
                                    className={inputClass}
                                    placeholder="R$ 0,00"
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className={labelClass}>Data de Aquisição</label>
                                <input type="date" name="dataAquisicao" value={formData.dataAquisicao} onChange={handleInputChange} className={inputClass} />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className={labelClass}>Garantia até</label>
                                <input type="date" name="garantiaAte" value={formData.garantiaAte} onChange={handleInputChange} className={inputClass} />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className={labelClass}>Fornecedor</label>
                                <select name="fornecedorId" value={formData.fornecedorId} onChange={handleInputChange} className={inputClass}>
                                    <option value="">Selecionar</option>
                                    {fornecedores.map((f) => (
                                        <option key={f.id} value={f.id}>{f.nome || f.nomeFornecedor}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className={labelClass}>Nº da Nota Fiscal</label>
                                <input type="text" name="numeroNotaFiscal" value={formData.numeroNotaFiscal} onChange={handleInputChange} className={inputClass} />
                            </div>
                        </div>
                    </div>

                    {/* Depreciação */}
                    <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                        <div className="text-stone-600 text-base font-medium">Depreciação (linear)</div>
                        <div className="text-slate-500 text-sm">
                            Informe a vida útil e o valor residual para o sistema calcular automaticamente o valor atual do bem.
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className={labelClass}>Vida útil (meses)</label>
                                <input
                                    type="number"
                                    min="0"
                                    name="vidaUtilMeses"
                                    value={formData.vidaUtilMeses}
                                    onChange={handleInputChange}
                                    className={inputClass}
                                    placeholder="Ex: 60"
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className={labelClass}>Valor Residual (R$)</label>
                                <input
                                    type="text"
                                    name="valorResidual"
                                    value={formData.valorResidual}
                                    onChange={handleCurrencyChange}
                                    className={inputClass}
                                    placeholder="R$ 0,00"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Fotos e Observações */}
                    <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                        <div className="text-stone-600 text-base font-medium">Fotos e Observações</div>

                        <div className="flex flex-col gap-1.5">
                            <label className={labelClass}>Fotos / Documentos do bem</label>
                            <input
                                type="file"
                                multiple
                                accept="image/*,.pdf"
                                onChange={handleFotosChange}
                                className="text-sm"
                            />
                            {fotos.length > 0 && (
                                <div className="text-slate-500 text-sm">{fotos.length} arquivo(s) selecionado(s)</div>
                            )}
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className={labelClass}>Observações</label>
                            <textarea name="observacoes" rows={3} value={formData.observacoes} onChange={handleInputChange} className={inputClass} />
                        </div>
                    </div>

                    {/* Botões */}
                    <div className="flex items-center w-full gap-4">
                        <div className="flex gap-4 ml-auto">
                            <button
                                type="button"
                                className="px-4 py-2 bg-stone-400 text-white text-sm font-medium rounded-md"
                                onClick={() => navigate("/patrimonios")}
                                disabled={isSaving}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2 disabled:opacity-50"
                                disabled={isSaving}
                            >
                                <img className="w-4 h-4" src="/images/icon/save.png" alt="Salvar" />
                                <span>{isSaving ? "Salvando..." : "Salvar"}</span>
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            <SuccessModal
                isVisible={showSuccessDialog}
                message="Patrimônio cadastrado com sucesso!"
                onClose={() => {
                    setShowSuccessDialog(false);
                    navigate("/patrimonios");
                }}
            />

            <AlertDialog
                isVisible={!!alertMessage}
                message={alertMessage}
                onClose={() => setAlertMessage("")}
            />
        </Template>
    );
};

export default CadastrarPatrimonio;
